/**
 * P1-04/05: TooltipProvider —— 新 UI 的统一 tooltip 体系。
 *
 * 取代旧 UIUtils.attachTooltip（core.js:2665-2722）：
 * - 桌面：hover / focus 触发，mouseleave / blur / Escape 关闭；
 * - 触控（P1-05）：长按 ~500ms 触发，点按空白处关闭；
 * - 内容每 tick 刷新（等价旧 game.tooltipUpdateFunc 机制，js/ui.js:627），
 *   经由 GameBridge.subscribeTick 实现，退订随关闭/卸载自动清理；
 * - 定位：锚点正下方，防出界钳制（对齐旧 maxTooltipTop/maxTooltipLeft 逻辑，
 *   core.js:2690-2695）；
 * - 内容为空字符串时不显示（对齐 core.js:2700-2702）。
 *
 * 内容为 HTML 字符串（旧 getTooltip 返回值原样复用），经 dangerouslySetInnerHTML
 * 渲染——内容全部来自引擎/i18n，与旧实现同等信任级别。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { subscribeTick } from "../../bridge/GameBridge";

export type TooltipContentProvider = () => string;

interface TooltipState {
  /** 内容生成器（每次 tick 重新调用） */
  getContent: TooltipContentProvider;
  /** 当前渲染的 HTML */
  html: string;
  /** 锚点屏幕位置 */
  anchorRect: { bottom: number; left: number };
  /** 触发锚点元素（用于"点空白关闭"的判定） */
  anchorEl: Element;
}

interface TooltipContextValue {
  show(anchorEl: Element, getContent: TooltipContentProvider): void;
  hide(): void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

const LONG_PRESS_MS = 500;
const VIEWPORT_PADDING = 25; // 对齐旧实现的 25px 边距（core.js:2690）

export function TooltipProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TooltipState | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const hide = useCallback(() => {
    setState(null);
    setPos(null);
  }, []);

  const show = useCallback((anchorEl: Element, getContent: TooltipContentProvider) => {
    const html = getContent();
    if (!html) {
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    setState({
      getContent,
      html,
      anchorRect: { bottom: rect.bottom, left: rect.left },
      anchorEl,
    });
  }, []);

  // 每 tick 刷新内容（对齐旧 game.tooltipUpdateFunc 机制）
  useEffect(() => {
    if (!state) {
      return;
    }
    return subscribeTick(() => {
      setState((prev) => (prev ? { ...prev, html: prev.getContent() } : prev));
    });
  }, [state != null]); // eslint-disable-line react-hooks/exhaustive-deps

  // 防出界钳制：渲染后量取 tooltip 尺寸，收进视口。
  // 注意：必须与 html 刷新解耦；仅在计算出的位置与当前 pos 不同时才 setPos，
  // 否则 tick 刷新 html 会触发 layout effect → setPos → re-render → 新 tick 的
  // 连锁反应，在快速点击/高频 tick 场景下触发 React Maximum update depth（#185）。
  useLayoutEffect(() => {
    if (!state || !tooltipRef.current) {
      return;
    }
    const el = tooltipRef.current;
    let top = state.anchorRect.bottom + 4;
    let left = state.anchorRect.left;
    const maxTop = window.innerHeight - el.offsetHeight - VIEWPORT_PADDING;
    const maxLeft = window.innerWidth - el.offsetWidth - VIEWPORT_PADDING;
    top = Math.max(0, Math.min(top, maxTop));
    left = Math.max(0, Math.min(left, maxLeft));
    if (!pos || pos.top !== top || pos.left !== left) {
      setPos({ top, left });
    }
    // pos 是本 effect 的产物，加入 deps 会在每次 setPos 后无意义地再跑一次；
    // 这里只在 state（含 html 刷新）变化时重新钳制，避免高频 tick 下连锁重渲染。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // 点空白 / Escape 关闭
  useEffect(() => {
    if (!state) {
      return;
    }
    const onPointerDown = (e: Event) => {
      const target = e.target as Node;
      if (tooltipRef.current?.contains(target) || state.anchorEl.contains(target)) {
        return;
      }
      hide();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        hide();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [state, hide]);

  return (
    <TooltipContext.Provider value={{ show, hide }}>
      {children}
      {state && (
        <div
          ref={tooltipRef}
          role="tooltip"
          className="button_tooltip fixed z-[10000] max-w-md border border-btn-border bg-surface px-2 py-1 text-xs text-text shadow-lg"
          style={{
            top: pos?.top ?? state.anchorRect.bottom + 4,
            left: pos?.left ?? state.anchorRect.left,
            visibility: pos ? "visible" : "hidden",
          }}
          dangerouslySetInnerHTML={{ __html: state.html }}
        />
      )}
    </TooltipContext.Provider>
  );
}

function useTooltipContext(): TooltipContextValue {
  const ctx = useContext(TooltipContext);
  if (!ctx) {
    throw new Error("Tooltip 锚点必须挂在 <TooltipProvider> 内");
  }
  return ctx;
}

export interface TooltipAnchorProps {
  /** 内容生成器；返回空字符串则不显示 */
  getTooltip?: TooltipContentProvider;
  className?: string;
  /** 透传到锚点 div 的 role（如 gridcell），供表格单元格类锚点使用 */
  role?: string;
  /** 透传原生 title（craft 行数值格/快捷链等旧 title 语义，left.jsx.js:489/493/316） */
  title?: string;
  /** 透传 onClick（快捷链等可点锚点；disabled 时不传即为旧版"死 div"语义） */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * hover/focus/长按激活期间附加的行内样式。
   * 对齐旧 attachResourceTooltip 的 mouseover bold 行为（game.js:4224-4230）：
   * 旧版无论 tooltip 内容是否为空都会加粗，此处同样在激活时即应用。
   */
  activeStyle?: CSSProperties;
  /** 透传基础行内样式（P3-01 GameButton 外层 btn 的 position/display/twoRow 边距） */
  style?: CSSProperties;
  /** 透传 tabIndex（P3-01 对齐旧按钮 domNode tabIndex=0，core.js:977） */
  tabIndex?: number;
  /** 透传 aria-description（P3-01 对齐 core.js:976） */
  ariaDescription?: string;
  /** 透传 onKeyPress（P3-01 对齐旧按钮 Enter 触发，core.js:1030-1034） */
  onKeyPress?: (e: ReactKeyboardEvent) => void;
  /** 透传 onMouseOver/onMouseOut（P3-01 hasResourceHover，core.js:1586-1595）；tooltip 逻辑之外追加调用 */
  onMouseOver?: (e: ReactMouseEvent) => void;
  onMouseOut?: (e: ReactMouseEvent) => void;
  children?: ReactNode;
}

/**
 * Tooltip 锚点包装组件：桌面 hover/focus、触控长按触发。
 * 无 getTooltip 时退化为普通 div。
 */
export function TooltipAnchor({ getTooltip, className, role, title, onClick, activeStyle, style, tabIndex, ariaDescription, onKeyPress, onMouseOver, onMouseOut, children }: TooltipAnchorProps) {
  const { show, hide } = useTooltipContext();
  const ref = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [active, setActive] = useState(false);

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  useEffect(() => clearLongPress, [clearLongPress]);

  const mergedStyle: CSSProperties | undefined =
    style || (active && activeStyle)
      ? { ...style, ...(active && activeStyle ? activeStyle : {}) }
      : undefined;

  if (!getTooltip) {
    return (
      <div
        className={className}
        role={role}
        title={title}
        onClick={onClick}
        style={mergedStyle}
        tabIndex={tabIndex}
        aria-description={ariaDescription}
        onKeyPress={onKeyPress}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={className}
      role={role}
      title={title}
      onClick={onClick}
      style={mergedStyle}
      tabIndex={tabIndex}
      aria-description={ariaDescription}
      onKeyPress={onKeyPress}
      onMouseEnter={() => {
        setActive(true);
        if (ref.current) {
          show(ref.current, getTooltip);
        }
      }}
      onMouseLeave={() => {
        setActive(false);
        hide();
      }}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onFocus={() => {
        setActive(true);
        if (ref.current) {
          show(ref.current, getTooltip);
        }
      }}
      onBlur={() => {
        setActive(false);
        hide();
      }}
      onTouchStart={() => {
        clearLongPress();
        longPressTimer.current = setTimeout(() => {
          if (ref.current) {
            setActive(true);
            show(ref.current, getTooltip);
          }
        }, LONG_PRESS_MS);
      }}
      onTouchEnd={() => {
        clearLongPress();
        setActive(false);
      }}
      onTouchMove={clearLongPress}
    >
      {children}
    </div>
  );
}
